import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAdminScopeOptional } from '@/contexts/AdminScopeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getFullName } from '@/lib/utils';
import { LogOut, Menu, Moon, Palette, Sun, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AdminTaxYearSelector } from './AdminTaxYearSelector';
import { AdminTenantSelector } from './AdminTenantSelector';
import { TaxYearSelector } from './TaxYearSelector';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
  sidebarContent?: React.ReactNode;
}

export function Header({ showMenuButton = false, sidebarContent }: HeaderProps) {
  const { user, logout, isAdmin } = useAuth();
  const { themeName, themeMode, setThemeName, setThemeMode, availableThemes } = useTheme();
  const adminScope = useAdminScopeOptional();

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center px-4 sm:px-6">
        {/* Mobile menu button */}
        {showMenuButton && sidebarContent && (
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="mr-2 md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              {sidebarContent}
            </SheetContent>
          </Sheet>
        )}

        {/* Logo: dark-logo for light theme, logo for dark theme */}
        <Link to={isAdmin() ? '/admin' : '/dashboard'} className="flex items-center gap-2">
          <img src="/dark-logo.png" alt="CenTax Filer" className="h-8 w-auto dark:hidden" />
          <img src="/logo.png" alt="CenTax Filer" className="h-8 w-auto hidden dark:block" />
          <span className="hidden font-semibold sm:inline-block">CenTax Filer</span>
        </Link>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {/* Admin: tenant + year; User: year only */}
          {adminScope ? (
            <>
              <AdminTenantSelector />
              <AdminTaxYearSelector />
            </>
          ) : (
            <TaxYearSelector />
          )}
          {/* Theme toggle: click to switch between light and dark */}
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')}
            aria-label={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user ? getInitials(user.firstName, user.lastName) : 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {getFullName(user ?? {})}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={isAdmin() ? '/admin/profile' : '/profile'} className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              {/* <DropdownMenuItem asChild>
                <Link to={isAdmin() ? '/admin/settings' : '/settings'} className="flex items-center">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem> */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Palette className="mr-2 h-4 w-4" />
                  Theme Color
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup value={themeName} onValueChange={(v) => setThemeName(v as typeof themeName)}>
                    {availableThemes.map((theme) => (
                      <DropdownMenuRadioItem key={theme.name} value={theme.name}>
                        {theme.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
